import settings from 'settings'
import { getRequest, patchRequest, postRequest } from 'features/common'

export type AgentModelProvider =
  | 'openai'
  | 'google'
  | 'bedrock'
  | 'snowflake'
  | 'databricks'
  | 'oracle'
  | 'anthropic'
  | 'azure'
  | 'custom'

export interface ProviderSettings {
  enabled: boolean
  apiKey?: string
  baseUrl?: string
  models: string[]
}

export type SettingsCartoAI = {
  enabled: boolean
} & Record<AgentModelProvider, ProviderSettings>

export interface TestConnectionRequest {
  apiKey: string
  baseUrl?: string
}

export interface TestConnectionResponse {
  success: boolean
  availableModels: string[]
  error?: {
    code: string
    message: string
    details: string
  }
}

export type SaveAIConfigRequest = {
  enabled: boolean
} & Record<AgentModelProvider, ProviderSettings>

/**
 * Get current AI configuration for the organization
 */
export const getSettingsCartoAI = async (): Promise<SettingsCartoAI | null> => {
  const { workspaceApiUrl } = settings
  const response: SettingsCartoAI = await getRequest(`${workspaceApiUrl}/settings/carto-ai`)
  return response
}

/**
 * Test connection to AI provider
 */
export const testConnection = async (request: TestConnectionRequest): Promise<TestConnectionResponse> => {
  // Mock implementation - replace with actual API call when backend is ready
  return new Promise((resolve) => {
    setTimeout(() => {
      // Simulate different outcomes based on API key
      if (request.apiKey.startsWith('sk-invalid')) {
        resolve({
          success: false,
          availableModels: [],
          error: {
            code: 'INVALID_API_KEY',
            message: "We couldn't connect to your provider",
            details: 'Invalid API key provided. Please check your OpenAI API key and try again.'
          }
        })
      } else if (request.apiKey.length < 10) {
        resolve({
          success: false,
          availableModels: [],
          error: {
            code: 'CONNECTION_FAILED',
            message: "We couldn't connect to your provider",
            details: 'Connection timeout. Please check your network connection and base URL.'
          }
        })
      } else {
        resolve({
          success: true,
          availableModels: ['GPT-4o', 'GPT-4o-mini', 'GPT-3.5-turbo', 'o1-preview'],
          error: undefined
        })
      }
    }, 1000)
  })
}

/**
 * Save AI configuration
 */
export const saveSettingsCartoAI = async (request: SaveAIConfigRequest): Promise<SettingsCartoAI> => {
  const { workspaceApiUrl } = settings
  return patchRequest(`${workspaceApiUrl}/settings/carto-ai`, request)
}

/**
 * Update SettingsCartoAI configuration
 */
export const updateSettingsCartoAI = async (request: Partial<SaveAIConfigRequest>): Promise<SettingsCartoAI> => {
  const { workspaceApiUrl } = settings
  return patchRequest(`${workspaceApiUrl}/settings/carto-ai`, request)
}

/**
 * Toggle AI enabled state
 */
export const toggleSettingsCartoAI = async (enabled: boolean): Promise<SettingsCartoAI> => {
  const { workspaceApiUrl } = settings
  return patchRequest(`${workspaceApiUrl}/settings/carto-ai`, { enabled })
}

export interface FetchModelsRequest {
  apiKey?: string
  baseUrl?: string
}

export interface ModelOption {
  id: string
  label: string
}

export interface FetchModelsResponse {
  models: ModelOption[]
}

/**
 * Fetch available models from the AI provider
 */
export const fetchAvailableModelsByProvider = async (
  provider: AgentModelProvider,
  request: FetchModelsRequest
): Promise<string[]> => {
  const { workspaceApiUrl } = settings

  // Get the raw models from the API
  const response: string[] = await postRequest(`${workspaceApiUrl}/carto-ai/${provider}/models`, request)
  return response
}

export interface AvailableModel {
  model: string
  source: string
  isCustom: boolean
  provider: AgentModelProvider
}

export const fetchAvailableModels = async (): Promise<AvailableModel[]> => {
  const { workspaceApiUrl } = settings
  return getRequest(`${workspaceApiUrl}/settings/carto-ai/models`)
}
